import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, type Account } from "../api";
import { Notice, PageHead } from "../components/ui";
import { useEntities } from "../entity";
import { useMe } from "../session";
import { useTxFlow } from "../txflow";
import { ConciliationTab } from "./conciliation/ConciliationTab";
import { Movements } from "./Movements";

/**
 * Contas e Extratos (DEC-112): visão integrada de receitas, despesas e transferências (realizadas, vencidas, agendadas e canceladas) com saldo
 * efetivado e projetado calculados no servidor, mais a área especializada de Conciliação. A antiga aba "Transferências" foi incorporada à lista
 * integrada (mesmas ações, no menu ⋮); o link antigo (?aba=transferencias) abre a lista já filtrada por transferências.
 */
export function Statements() {
  const { me } = useMe();
  const realm = me.user.realm;
  const { current, loading } = useEntities();
  const flow = useTxFlow();
  const [sp, setSp] = useSearchParams();
  const tab = sp.get("aba") === "conciliacao" ? "conciliacao" : "movimentacoes";
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [error, setError] = useState(false);
  const canWrite = current?.permission === "total";

  const loadAccounts = useCallback(async () => {
    if (!current) return;
    setAccounts(await api<Account[]>("GET", `/api/${realm}/accounts?entityId=${current.id}`));
  }, [realm, current]);
  useEffect(() => { setError(false); void loadAccounts().catch(() => setError(true)); }, [loadAccounts, flow.version]);

  if (loading) return <div className="muted">Carregando…</div>;
  if (!current) return <Notice kind="error">Nenhuma entidade disponível para o seu usuário.</Notice>;

  return (
    <>
      <PageHead title="Contas e Extratos" sub={`${current.name} — receitas, despesas e transferências, realizadas e planejadas, com saldo efetivado e projetado.`} />
      <div className="seg" role="tablist" aria-label="Seções" style={{ marginBottom: 16 }}>
        <button role="tab" aria-selected={tab === "movimentacoes"} aria-pressed={tab === "movimentacoes"} data-tab="movimentacoes" onClick={() => setSp({}, { replace: true })}>Movimentações</button>
        <button role="tab" aria-selected={tab === "conciliacao"} aria-pressed={tab === "conciliacao"} data-tab="conciliacao" onClick={() => setSp({ aba: "conciliacao" }, { replace: true })}>Conciliação</button>
      </div>
      {error && <Notice kind="error">Não foi possível carregar as contas. Tente novamente.</Notice>}
      {tab === "conciliacao" && <ConciliationTab realm={realm} accounts={accounts} canWrite={canWrite} />}
      {tab === "movimentacoes" && <Movements realm={realm} entityId={current.id} accounts={accounts} canWrite={canWrite} initialTypes={sp.get("aba") === "transferencias" ? ["transferencia"] : undefined} />}
    </>
  );
}
